import { useMemo } from "react";
import { useLCID } from "../../../locale";
import {
  isMeasureItemDTO,
  isFieldItemDTO,
  useDataSourcesQuery,
  useDimensionItemsQuery,
  useDimensionssQuery,
  useBulkExportMutation,
  useSavedExportSettingsQuery,
  IsavedExportSettingsDTO,
} from "../../api";
import { useBulkExportContext } from "../../providers";
import { virtualConfiguration } from "../BulkExportStepper";
import { useBulkExportFavouriteQueryParams } from "../../shared/hooks/useBulkExportFavouriteQueryParams";
import { BulkExportFormat } from "../../types";

export function useExportTriggerButton() {
  const context = useBulkExportContext();
  const { favoriteBulkId } = useBulkExportFavouriteQueryParams();

  const lcid = useLCID();
  const state = context?.state;

  const parameters = state?.currentFavouriteSettings?.mark?.parameters as IsavedExportSettingsDTO;
  const {
    dimensions: favouriteDimentions,
    measures: favouriteMeasures,
    format: favouriteFormat,
    cubeId: favouriteCubeId,
  } = parameters ?? {};

  const { dataSourceId, tab, savedExportSettingsId } = state ?? {};
  const configuration = favoriteBulkId
    ? favouriteDimentions?.find((d) => d.id === virtualConfiguration.hierarchyIdentifier)
    : state?.dimensions.find((d) => d.id === virtualConfiguration.hierarchyIdentifier);

  const { isDataSourcesLoading, dataSources } = useDataSourcesQuery();
  const { savedExportSettings } = useSavedExportSettingsQuery();
  const { isDimensionsLoading, dimensions } = useDimensionssQuery(state?.dataSourceId);
  const { dimensionItems: configurationItems } = useDimensionItemsQuery(lcid, dataSourceId, virtualConfiguration.identifier);
  const { bulkExport } = useBulkExportMutation();

  return useMemo(() => {
    const selectedMeasures = configurationItems?.filter(isMeasureItemDTO).filter((it) => configuration?.selection.includes(it.id));
    const selectedFields = configurationItems?.filter(isFieldItemDTO).filter((it) => configuration?.selection.includes(it.id));
    const hasEmail = Boolean(state?.email);

    const checkSelection = (stateDimentions) => {
      if (!dimensions) {
        return false;
      }
      return dimensions.every((dim) => {
        const selection = stateDimentions?.find((d) => d.id === dim.hierarchyIdentifier)?.selection;
        return dim.visible === false || Boolean(selection?.length);
      });
    };

    const hasSelection = favoriteBulkId ? checkSelection(favouriteDimentions) : checkSelection(state?.dimensions);

    const hasMeasures = favoriteBulkId ? favouriteMeasures?.length : selectedMeasures?.length;
    const hasFields = favoriteBulkId ? favouriteDimentions?.length : selectedFields?.length;

    const disabled = isDimensionsLoading || isDataSourcesLoading || !(hasEmail && hasSelection && hasMeasures && hasFields);

    const startExport = () => {
      const dataSource = dataSources!.find((d) => d.id === dataSourceId)!;
      const cubeId = dataSource.identifier;
      const currentDimensions = favoriteBulkId ? favouriteDimentions : state!.dimensions;
      const finalDimension = currentDimensions!
        .filter((d) => dimensions?.some((dim) => dim.hierarchyIdentifier === d.id))
        .map((dimension) => {
          const dimensionId = dimension.id;
          const fieldNameItem = selectedFields?.find((field) => field.id.toString().includes(dimensionId));
          const fieldName = fieldNameItem?.name ?? "";
          return {
            ...dimension,
            fieldName,
            isSelected: !!fieldName,
          };
        });

      const finalMeasures = selectedMeasures!.map((measure) => ({
        fieldName: measure.name,
        id: measure.id as string,
        isSelected: true,
      }));
      const name = tab ? savedExportSettings?.find((el) => el.id === savedExportSettingsId)?.name : undefined;
      const formate = favoriteBulkId
        ? favouriteFormat === 0
          ? BulkExportFormat.XLSX
          : BulkExportFormat.CSV
        : (state!.format as "xlsx" | "csv");
      const email = state!.email!;

      bulkExport({
        formate,
        cubeId: favoriteBulkId ? favouriteCubeId! : cubeId,
        email,
        dimensions: finalDimension,
        measures: finalMeasures,
        name,
      });
    };
    return { disabled, startExport };
  }, [bulkExport, state, isDataSourcesLoading, dataSources, isDimensionsLoading, dimensions, configuration, configurationItems]);
}
